import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Rarity, type Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Auction } from '@sorare/core/src/atoms/icons/Auction';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { InstantBuy } from '@sorare/core/src/atoms/icons/InstantBuy';
import { Transfer } from '@sorare/core/src/atoms/icons/Transfer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyS, BrandL, BrandT } from '@sorare/core/src/atoms/typography';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { getRarityColor, getRarityRGBColor } from '@sorare/core/src/lib/colors';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

const Border = styled(Horizontal)`
  --rarity-color: var(--c-rgb-limited);

  background: linear-gradient(
    180deg,
    var(--c-nd-150) 0%,
    var(--c-nd-150) 0%,
    rgba(var(--rarity-color), 0.4) 100%
  );
  padding: 1px;
  border-radius: var(--double-unit);
`;
const Root = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-black);
  width: 100%;
  justify-content: space-between;
  padding: var(--intermediate-unit);
  border-radius: var(--double-unit);

  @container ${laptopAndAbove} {
    flex-direction: row;
    align-items: stretch;
    padding: var(--double-unit);
  }
`;
const TitleBlock = styled(Vertical)`
  gap: var(--unit);
  width: 100%;
  padding-bottom: var(--triple-unit);
  @media ${laptopAndAbove} {
    align-items: stretch;
    justify-content: center;
    padding-bottom: 0;
    padding-right: var(--double-unit);
  }
`;
const Block = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  width: 100%;
  padding: var(--double-unit) 0;
  justify-content: center;

  @container ${laptopAndAbove} {
    justify-content: flex-start;
    padding: 0 var(--double-unit);
    &:last-child {
      padding-right: 0;
    }
  }
  &:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-150);
    @container ${laptopAndAbove} {
      border-bottom: none;
      border-right: 1px solid var(--c-nd-150);
    }
  }
`;
const LeftContent = styled(BrandL)`
  text-align: left;
`;
type Props = {
  rarity: Rarity;
  sport: Sport;
};

const getRarityFilteredUrl = (route: string, sport: Sport, rarity: Rarity) => {
  return `${generateSportPath(route, {
    sport,
  })}?${buildFilterQuery({
    [SEARCH_PARAMS.RARITY]: rarity,
  })}`;
};

const useMarketRoutes = ({
  sport,
  rarity,
}: {
  sport: Sport;
  rarity: Rarity;
}) => {
  const auctionMarketUrl = getRarityFilteredUrl(
    ANY_SPORT_MARKET_SHOP_AUCTIONS,
    sport,
    rarity
  );
  const instantBuyUrl = getRarityFilteredUrl(
    ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
    sport,
    rarity
  );
  const secondaryMarketUrl = getRarityFilteredUrl(
    ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
    sport,
    rarity
  );
  const options = [
    {
      key: 'auctions',
      label: <FormattedMessage {...navLabels.auctions} />,
      url: auctionMarketUrl,
    },
    {
      key: 'instantBuy',
      label: <FormattedMessage {...transferMarket.instantBuy} />,
      url: instantBuyUrl,
    },
    {
      key: 'secondaryMarket',
      label: <FormattedMessage {...transferMarket.transfer} />,
      url: secondaryMarketUrl,
    },
  ];

  return {
    options,
    auctionMarketUrl,
    instantBuyUrl,
    secondaryMarketUrl,
  };
};

export const MarketRoutesBlock = ({ rarity, sport }: Props) => {
  const {
    flags: { useNewMarketRoutes = false },
  } = useFeatureFlags();
  const isTabletAndAbove = useIsTabletAndAbove();

  const { auctionMarketUrl, instantBuyUrl, secondaryMarketUrl } =
    useMarketRoutes({ sport, rarity });

  const color = getRarityColor(rarity);

  if (!useNewMarketRoutes || !isTabletAndAbove) {
    return null;
  }

  return (
    <Border
      style={{ '--rarity-color': getRarityRGBColor(rarity) } as CSSProperties}
    >
      <Root>
        <TitleBlock>
          <LeftContent>
            <FormattedMessage
              id="MarketRoutesBlock.title"
              defaultMessage="<yellow>Buy players</yellow>{br}your way"
              values={{
                yellow: chunks => <span style={{ color }}>{chunks}</span>,
                br: <br />,
              }}
            />
          </LeftContent>
        </TitleBlock>
        <Block className="border" to={auctionMarketUrl}>
          <Auction size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...navLabels.auctions} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.auctions.desc"
                defaultMessage="Bid & win at the best price from Sorare"
              />
            </BodyS>
          </Vertical>
        </Block>
        <Block className="border" to={instantBuyUrl}>
          <InstantBuy size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...transferMarket.instantBuy} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>
            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.instantBuy.desc"
                defaultMessage="Buy instantly from Sorare"
              />
            </BodyS>
          </Vertical>
        </Block>
        <Block to={secondaryMarketUrl}>
          <Transfer size={2} color={color} />
          <Vertical gap={0.5}>
            <SBHorizontal>
              <BrandT>
                <FormattedMessage {...transferMarket.transfer} />
              </BrandT>
              <ChevronRightBold />
            </SBHorizontal>

            <BodyS color="var(--c-nd-600)">
              <FormattedMessage
                id="MarketRoutesBlock.secondaryMarket.desc"
                defaultMessage="Deals from other Managers"
              />
            </BodyS>
          </Vertical>
        </Block>
      </Root>
    </Border>
  );
};

const StyledTabBar = styled(TabBar)`
  padding: var(--unit) 0;
  --default-bg-color: var(--c-nd-200);
  --hover-bg-color: var(--c-nd-300);
`;

const StyledTab = styled(Tab)`
  > * {
    ${thinMaterial}
  }
`;

export const MobileMarketRoutesBlock = ({ rarity, sport }: Props) => {
  const { options } = useMarketRoutes({ sport, rarity });
  const isTabletAndAbove = useIsTabletAndAbove();
  if (
    isTabletAndAbove ||
    [Rarity.custom_series, Rarity.common].includesValue(rarity)
  ) {
    return null;
  }
  return (
    <StyledTabBar variant="segmented">
      {options.map(option => (
        <StyledTab key={option.key} to={option.url}>
          {option.label}
        </StyledTab>
      ))}
    </StyledTabBar>
  );
};
